/**
 * Macro #25 — Profile ▸ Analytics (private owner/creator cockpit tab).
 *
 * Premium, compact, server-authoritative. Every number is a READ-ONLY canonical
 * aggregate for the session wallet. Rewards/Influence render only when the
 * Macro #23 economy is LIVE — otherwise a truthful "Not live yet" card (never
 * fabricated zeros or dead charts). Charts appear only when they hold real data.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  analyticsApi,
  type AnalyticsSelf,
  type Range,
  type Timeseries,
} from "../../lib/analytics-api";
import { RANGE_LABELS, fmtInt, usdc } from "../analytics-format";
import { AreaTrend, StackedRewardBars } from "../analytics/MiniCharts";
import { Card, StatTile } from "./primitives";
import { useI18n } from "../../i18n";

function Section({ title, right, children, testId }: { title: string; right?: React.ReactNode; children: React.ReactNode; testId?: string }) {
  return (
    <div data-testid={testId} className="mt-6 first:mt-0">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

export function AnalyticsPanel() {
  const { t } = useI18n();
  const [self, setSelf] = useState<AnalyticsSelf | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [range, setRange] = useState<Range>("30d");
  const [referrals, setReferrals] = useState<Timeseries | null>(null);
  const [rewards, setRewards] = useState<Timeseries | null>(null);
  const [influence, setInfluence] = useState<Timeseries | null>(null);
  const reqRef = useRef(0);

  const loadSelf = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setSelf(await analyticsApi.self());
    } catch {
      setError(true);
      setSelf(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSelf();
  }, [loadSelf]);

  const rewardsLive = self?.rewards.live === true;

  useEffect(() => {
    if (!self) return;
    const rid = ++reqRef.current;
    void (async () => {
      try {
        const ref = await analyticsApi.timeseries(range, "referrals");
        if (rid === reqRef.current) setReferrals(ref);
      } catch {
        /* keep prior */
      }
      if (rewardsLive) {
        try {
          const [rw, inf] = await Promise.all([
            analyticsApi.timeseries(range, "rewards"),
            analyticsApi.timeseries(range, "influence"),
          ]);
          if (rid === reqRef.current) {
            setRewards(rw);
            setInfluence(inf);
          }
        } catch {
          /* keep prior */
        }
      }
    })();
  }, [self, range, rewardsLive]);

  if (loading) {
    return (
      <Card title={t("profile.analytics.title")} testId="analytics-panel">
        <div data-testid="analytics-loading" className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-xl border border-white/5 bg-white/[0.02]" />
          ))}
        </div>
      </Card>
    );
  }

  if (error || !self) {
    return (
      <Card title={t("profile.analytics.title")} testId="analytics-panel">
        <div data-testid="analytics-error" className="rounded-xl border border-white/10 bg-white/[0.02] p-6 text-center">
          <p className="text-[13px] text-white/70">{t("profile.analytics.loadError")}</p>
          <button
            type="button"
            data-testid="analytics-retry"
            onClick={() => void loadSelf()}
            className="mt-3 rounded-full border border-white/15 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-white/75 hover:bg-white/5"
          >
            {t("profile.analytics.retry")}
          </button>
        </div>
      </Card>
    );
  }

  const c = self.current;
  const l = self.lifetime;

  return (
    <Card title={t("profile.analytics.title")} testId="analytics-panel">
      <Section title={t("profile.analytics.sections.land")}>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <StatTile label={t("profile.analytics.kpi.parcelsOwned")} value={fmtInt(c.parcels_owned)} testId="analytics-kpi-parcels-owned" tint="text-teal-200" />
          <StatTile label={t("profile.analytics.kpi.totalArea")} value={fmtInt(c.total_area)} testId="analytics-kpi-total-area" sub={t("profile.analytics.cells")} />
          <StatTile label={t("profile.analytics.kpi.primaryAcq")} value={fmtInt(l.primary_acquisitions)} testId="analytics-kpi-primary-acquisitions" />
          <StatTile label={t("profile.analytics.kpi.acqVolume")} value={usdc(l.primary_acquisition_volume_micro)} testId="analytics-kpi-acquisition-volume" sub="USDC" tint="text-teal-200" />
        </div>
      </Section>

      <Section title={t("profile.analytics.sections.creator")}>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <StatTile label={t("profile.analytics.kpi.ownedTerritories")} value={fmtInt(c.owned_territories)} testId="analytics-kpi-owned-territories" />
          <StatTile label={t("profile.analytics.kpi.publishedTerritories")} value={fmtInt(c.published_territories)} testId="analytics-kpi-published-territories" tint="text-violet-200" />
          <StatTile label={t("profile.analytics.kpi.createdArtworks")} value={fmtInt(c.created_artworks)} testId="analytics-kpi-created-artworks" />
          <StatTile label={t("profile.analytics.kpi.qualifiedReferrals")} value={fmtInt(l.qualified_referrals)} testId="analytics-kpi-qualified-referrals" tint="text-teal-200" />
        </div>
      </Section>

      <Section
        title={t("profile.analytics.sections.trends")}
        testId="analytics-trends"
        right={
          <div data-testid="analytics-range" className="flex gap-1">
            {RANGE_LABELS.map(([id, label]) => (
              <button
                key={id}
                type="button"
                data-testid={`analytics-range-${id}`}
                data-active={range === id}
                onClick={() => setRange(id)}
                className={`rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.1em] transition-colors ${
                  range === id ? "bg-white/10 text-white" : "text-white/40 hover:text-white/70"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        }
      >
        <div className="rounded-xl border border-white/8 bg-white/[0.015] p-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[12px] text-white/60">{t("profile.analytics.referralQ")}</span>
            <span data-testid="analytics-referrals-total" className="font-mono text-[11px] tabular-nums text-teal-200">
              {t("profile.analytics.inRange", { n: fmtInt(referrals?.total ?? 0) })}
            </span>
          </div>
          <AreaTrend points={referrals?.points ?? []} testId="analytics-chart-referrals" emptyLabel={t("profile.analytics.noReferrals")} />
        </div>
      </Section>

      <Section title={t("profile.analytics.sections.rewards")}>
        {rewardsLive && self.rewards.live && self.influence.live ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <StatTile label={t("profile.analytics.kpi.influence")} value={fmtInt(self.influence.influence_points)} testId="analytics-kpi-influence" tint="text-violet-200" sub={self.influence.badge ?? t("profile.badge.none")} />
              <StatTile label={t("profile.analytics.kpi.rewardsAvailable")} value={usdc(self.rewards.available_micro)} testId="analytics-kpi-rewards-available" sub="USDC" tint="text-teal-200" />
              <StatTile label={t("profile.analytics.kpi.totalRewards")} value={usdc(self.rewards.total_qualified_micro)} testId="analytics-kpi-rewards-total" sub="USDC" />
              <StatTile label={t("profile.analytics.kpi.pendingFunding")} value={usdc(self.rewards.pending_funding_micro)} testId="analytics-kpi-rewards-pending" sub="USDC" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-white/8 bg-white/[0.015] p-4">
                <div className="mb-2 text-[12px] text-white/60">{t("profile.analytics.rewardsByType")}</div>
                <StackedRewardBars points={rewards?.points ?? []} testId="analytics-chart-rewards" />
              </div>
              <div className="rounded-xl border border-white/8 bg-white/[0.015] p-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[12px] text-white/60">{t("profile.analytics.influenceGrowth")}</span>
                  <span className="font-mono text-[11px] tabular-nums text-violet-200">{self.influence.badge ?? "—"}</span>
                </div>
                <AreaTrend points={influence?.points ?? []} testId="analytics-chart-influence" tint="#a78bfa" emptyLabel={t("profile.analytics.noInfluence")} />
              </div>
            </div>
          </div>
        ) : (
          <div data-testid="analytics-rewards-notlive" className="rounded-xl border border-white/10 bg-white/[0.02] p-6 text-center">
            <p className="text-[13px] text-white/70">{t("profile.analytics.notLiveTitle")}</p>
            <p className="mt-1 text-[12px] text-white/45">
              {t("profile.analytics.notLiveBody")}
            </p>
          </div>
        )}
      </Section>
    </Card>
  );
}
